import type { PaneKey } from "components/Nav/nav.model";
import { DEFAULT_SUB, findNavItem, HOME_PANE, VISIBLE_NAV_ITEMS, visibleSubs } from "components/Nav/nav.model";
import { DEFAULT_DETAIL_TAB, VISIBLE_DETAIL_TABS } from "components/ShipmentDetail/detail.model";

/**
 * The menu is mirrored in the address bar as `/cabinet/{pane}/{sub}` so a pane
 * survives a reload and back/forward work. The host page tells us where it
 * mounted the widget; `/cabinet` is what this site uses.
 */
const readBasePath = (): string => {
  const declared = document.getElementById("cabinet_root")?.dataset.basePath;

  return `/${(declared || "/cabinet").trim().replace(/^\/+|\/+$/g, "")}`;
};

export const BASE_PATH = readBasePath();

/**
 * Sections the cabinet used to serve. Integrations was split into Qubics Store,
 * My Qubics and API Keys, and none of its three tabs survives as a page, so
 * every one of its URLs opens the dashboard instead.
 */
const RETIRED_SLUGS = new Set(["integrations"]);

/**
 * Short addresses other systems link to. The back office sends Stripe Checkout,
 * the billing portal and the 402 `buy_url` to `/cabinet/billing`; App rewrites
 * it to the pane's own path in place, keeping the query (`?checkout=success`).
 */
export const ROUTE_ALIASES: Record<string, PaneKey> = {
  billing: "billing",
};

/** One shipment open on its detail page, and the tab showing. */
export type ShipmentRoute = {
  id: number;
  tab: string;
};

export type CabinetRoute = {
  pane: PaneKey;
  /** Null means "whatever this pane opens on" — the path left it out. */
  sub: string | null;
  /** Set only under My Shipments, when the path names a shipment. */
  shipment: ShipmentRoute | null;
};

/** The sub-item a shipment's page hangs under — My Shipments. */
const SHIPMENT_LIST_SUB = "shipments";

const NUMERIC_ID = /^[1-9]\d*$/;

const readSegments = (pathname: string): string[] =>
  pathname
    .slice(BASE_PATH.length)
    .split("/")
    .filter(Boolean)
    .map((segment) => decodeURIComponent(segment));

/**
 * True when the address bar still names a retired section. An unknown slug
 * already opens the dashboard, but leaving the old path showing would keep a
 * dead URL in circulation — App rewrites it with `replaceRoute`, which is also
 * why this must not go through `pushRoute`: a pushed entry would give Back
 * something to bounce off, straight back onto the path we just left.
 */
export const isRetiredPath = (pathname: string = window.location.pathname): boolean => {
  if (!pathname.startsWith(BASE_PATH)) return false;

  const [paneSlug] = readSegments(pathname);

  return paneSlug !== undefined && RETIRED_SLUGS.has(paneSlug);
};

/** True when the address bar uses a short alias instead of the pane's own path. */
export const isAliasPath = (pathname: string = window.location.pathname): boolean => {
  if (!pathname.startsWith(BASE_PATH)) return false;

  const [paneSlug] = readSegments(pathname);

  return paneSlug !== undefined && ROUTE_ALIASES[paneSlug] !== undefined;
};

export const readRoute = (): CabinetRoute => {
  const { pathname } = window.location;

  if (!pathname.startsWith(BASE_PATH)) {
    return { pane: HOME_PANE, sub: null, shipment: null };
  }

  const [paneSlug, subSlug, idSlug, tabSlug] = readSegments(pathname);

  if (paneSlug === undefined || RETIRED_SLUGS.has(paneSlug)) {
    return { pane: HOME_PANE, sub: null, shipment: null };
  }

  const alias = ROUTE_ALIASES[paneSlug];
  if (alias !== undefined) {
    return { pane: alias, sub: null, shipment: null };
  }

  // An unknown or hidden pane is not worth a 404 inside the widget — the menu
  // just opens where it would have on a bare /cabinet.
  const item = VISIBLE_NAV_ITEMS.find((navItem) => navItem.slug === paneSlug);
  if (item === undefined) {
    return { pane: HOME_PANE, sub: null, shipment: null };
  }

  // An unbuilt sub-item is not addressable either: its URL opens the pane on
  // whatever it lands on by default.
  const sub = visibleSubs(item).find((subItem) => subItem.slug === subSlug);
  const opensShipment =
    item.key === "shipments" && sub?.key === SHIPMENT_LIST_SUB && idSlug !== undefined && NUMERIC_ID.test(idSlug);

  // A hidden or unknown tab opens the shipment on its default one; App then
  // corrects the address bar to match (see `isCanonicalShipmentPath`).
  const tab = VISIBLE_DETAIL_TABS.find((detailTab) => detailTab.slug === tabSlug)?.key ?? DEFAULT_DETAIL_TAB;

  return {
    pane: item.key,
    sub: sub?.key ?? null,
    shipment: opensShipment ? { id: Number(idSlug), tab } : null,
  };
};

export const buildPath = (pane: PaneKey | null, sub: string | null): string => {
  if (pane === null) {
    return BASE_PATH;
  }

  const item = findNavItem(pane);

  if (item === undefined || item.slug === "" || item.hidden === true) {
    return BASE_PATH;
  }

  // The section's own landing tab stays implicit, so /cabinet/shipments and
  // /cabinet/shipments/my-shipments are not two URLs for one screen.
  const subSlug =
    sub !== null && sub !== DEFAULT_SUB[pane]
      ? visibleSubs(item).find((subItem) => subItem.key === sub)?.slug
      : undefined;

  return subSlug === undefined ? `${BASE_PATH}/${item.slug}` : `${BASE_PATH}/${item.slug}/${subSlug}`;
};

/**
 * `/cabinet/shipments/my-shipments/{id}` and, for any tab but the default,
 * `/{tab}` after it. Unlike a bare section, the list's own slug stays in the
 * path: it is what says the next segment is a shipment id.
 */
export const buildShipmentPath = ({ id, tab }: ShipmentRoute): string => {
  const item = findNavItem("shipments");
  const listSlug = visibleSubs(item).find((subItem) => subItem.key === SHIPMENT_LIST_SUB)?.slug;
  const tabSlug =
    tab === DEFAULT_DETAIL_TAB ? undefined : VISIBLE_DETAIL_TABS.find((detailTab) => detailTab.key === tab)?.slug;
  const path = `${BASE_PATH}/${item?.slug}/${listSlug}/${id}`;

  return tabSlug === undefined ? path : `${path}/${tabSlug}`;
};

/**
 * False when the address bar names a shipment in a form `buildShipmentPath`
 * would not write — a hidden or unknown tab, the default tab spelled out, a
 * trailing segment. App rewrites it in place with `replaceShipmentRoute`.
 */
export const isCanonicalShipmentPath = (): boolean => {
  const { shipment } = readRoute();

  return shipment === null || buildShipmentPath(shipment) === window.location.pathname;
};

const writePath = (method: "pushState" | "replaceState", path: string): void => {
  if (path === window.location.pathname) return;

  window.history[method](null, "", `${path}${window.location.search}${window.location.hash}`);
};

export const pushRoute = (pane: PaneKey | null, sub: string | null): void => {
  writePath("pushState", buildPath(pane, sub));
};

/** Correct the address bar without leaving the old URL in the back stack. */
export const replaceRoute = (pane: PaneKey | null, sub: string | null): void => {
  writePath("replaceState", buildPath(pane, sub));
};

export const pushShipmentRoute = (shipment: ShipmentRoute): void => {
  writePath("pushState", buildShipmentPath(shipment));
};

export const replaceShipmentRoute = (shipment: ShipmentRoute): void => {
  writePath("replaceState", buildShipmentPath(shipment));
};
